import { useCacheHelpers } from '../apollo/cache-helpers'
import { useBoreholeFieldContainers } from '../borehole-field-container/use-borehole-field-containers'
import {
  BoreholeFieldContainerInput,
  CreateEnergyBusInput,
  EnergyBusFragment,
  EnergyBusFragmentDoc,
  HeatingOrCoolingSource,
  HourSeriesFragment,
  HourSeriesFragmentDoc,
  HybridHeatPumpParameters,
  ProjectVersionFragment,
  UpdateEnergyBusInput,
  useCreateEnergyBusMutation,
  useDeleteEnergyBusMutation,
  useUpdateEnergyBusBoreholeFieldMutation,
  useUpdateEnergyBusMutation,
} from '../generated/graphql'
import { useProviders } from '../provider/use-providers'

type EnergyBusMutations = {
  isCreateLoading: boolean
  isUpdateLoading: boolean
  isUpdateBoreholeFieldLoading: boolean
  isDeleteLoading: boolean

  createEnergyBus: (
    projectVersion: ProjectVersionFragment,
    input: CreateEnergyBusInput,
  ) => Promise<EnergyBusFragment>

  updateEnergyBus: (
    id: string,
    input: UpdateEnergyBusInput,
  ) => Promise<EnergyBusFragment>

  updateEnergyBusBoreholeField: (
    id: string,
    input: BoreholeFieldContainerInput,
  ) => Promise<EnergyBusFragment>

  deleteEnergyBus: (
    projectVersion: ProjectVersionFragment,
    id: string,
  ) => Promise<void>
}

export function useEnergyBus(): EnergyBusMutations {
  const [createEnergyBus, { loading: isCreateLoading }] =
    useCreateEnergyBusMutation()
  const [updateEnergyBus, { loading: isUpdateLoading }] =
    useUpdateEnergyBusMutation()
  const [
    updateEnergyBusBoreholeField,
    { loading: isUpdateBoreholeFieldLoading },
  ] = useUpdateEnergyBusBoreholeFieldMutation()
  const [deleteEnergyBus, { loading: isDeleteLoading }] =
    useDeleteEnergyBusMutation()
  const { updateProvider } = useProviders()
  const { setField, getFragment } = useCacheHelpers()
  const { getContainerForUpdateOptimisticResponse } =
    useBoreholeFieldContainers()

  const disconnectProvidersFromEnergyBus = async (
    projectVersion: ProjectVersionFragment,
  ) => {
    const hybridHeatPumpProvidersWithEnergyBusSource = projectVersion.buildings
      .flatMap((building) => building.providers)
      .filter(
        (provider) =>
          provider.parameters.__typename === 'HybridHeatPumpParameters' &&
          (provider.parameters.baseHeatSource ===
            HeatingOrCoolingSource.EnergyBus ||
            provider.parameters.coolingSource ===
              HeatingOrCoolingSource.EnergyBus),
      )

    const disconnectHeatSource = (heatSource?: HeatingOrCoolingSource | null) =>
      heatSource !== HeatingOrCoolingSource.EnergyBus
        ? heatSource
        : HeatingOrCoolingSource.None

    await Promise.all(
      hybridHeatPumpProvidersWithEnergyBusSource.map((provider) => {
        const parameters = provider.parameters as HybridHeatPumpParameters

        return updateProvider(
          provider.id,
          {
            parameters: {
              hybridHeatPump: {
                ...provider.parameters,
                __typename: undefined,
                baseHeatSource: disconnectHeatSource(parameters.baseHeatSource),
                coolingSource: disconnectHeatSource(parameters.coolingSource),
                heatCircuits: parameters.heatCircuits?.map((heatCircuit) => ({
                  ...heatCircuit,
                  __typename: undefined,
                })),
              } as HybridHeatPumpParameters,
            },
          },
          null,
        )
      }),
    )
  }

  return {
    isCreateLoading,
    isUpdateLoading,
    isUpdateBoreholeFieldLoading,
    isDeleteLoading,

    async createEnergyBus(
      projectVersion: ProjectVersionFragment,
      input: CreateEnergyBusInput,
    ) {
      const result = await createEnergyBus({
        variables: {
          projectVersionId: projectVersion.id,
          input,
        },

        update: (_, mutationResult) => {
          const energyBus = mutationResult.data?.createEnergyBus
          if (!energyBus) {
            return
          }

          setField(EnergyBusFragmentDoc, energyBus, projectVersion, 'energyBus')
        },
      })

      if (!result.data) {
        throw new Error('Energy bus creation failed')
      }

      return result.data.createEnergyBus
    },

    async updateEnergyBus(id: string, input: UpdateEnergyBusInput) {
      const result = await updateEnergyBus({
        variables: { id, input },

        optimisticResponse: () => {
          const energyBusFragment = getFragment<EnergyBusFragment>(
            id,
            EnergyBusFragmentDoc,
          )

          const wasteHourSeries = input.wasteHourSeriesId
            ? getFragment<HourSeriesFragment>(
                input.wasteHourSeriesId,
                HourSeriesFragmentDoc,
              )
            : null

          return {
            updateEnergyBus: {
              ...energyBusFragment,
              wasteHourSeries,
            },
          }
        },
      })

      if (!result.data) {
        throw new Error('Energy bus update failed')
      }

      return result.data.updateEnergyBus
    },

    async updateEnergyBusBoreholeField(
      id: string,
      input: BoreholeFieldContainerInput,
    ) {
      const result = await updateEnergyBusBoreholeField({
        variables: { id, input },

        optimisticResponse: () => {
          const energyBusFragment =
            getContainerForUpdateOptimisticResponse<EnergyBusFragment>(
              id,
              input,
              EnergyBusFragmentDoc,
            )

          return { updateEnergyBusBoreholeField: energyBusFragment }
        },
      })

      if (!result.data) {
        throw new Error('Energy bus borehole field update failed')
      }

      return result.data.updateEnergyBusBoreholeField
    },

    async deleteEnergyBus(projectVersion: ProjectVersionFragment, id: string) {
      await disconnectProvidersFromEnergyBus(projectVersion)

      await deleteEnergyBus({
        variables: { id },

        update: () => {
          setField(EnergyBusFragmentDoc, null, projectVersion, 'energyBus')
        },

        optimisticResponse: {
          deleteEnergyBus: true,
        },
      })
    },
  }
}
